import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIntl } from 'react-intl';

import { Badge } from 'src/registry/ui/Badge';
import { cn } from 'src/utils/shadcn';

import type { CellContext } from '@tanstack/react-table';
import type { DataTableColumnHeaderMeta } from 'src/registry/components/table/types';
import type { BadgeOptions } from 'src/registry/types/badges';

interface CellBadgeProps<TData, TValue> extends CellContext<TData, TValue> {
    badgeOptions?: BadgeOptions;
}

// We used to piggyback the meta property of a column def, but we can now also
// handle them as a prop to the CellBadge component.
export function CellBadge<TData, TValue>({ column, cell, badgeOptions }: CellBadgeProps<TData, TValue>) {
    const { formatMessage } = useIntl();
    const options =
        badgeOptions ?? (column.columnDef.meta as DataTableColumnHeaderMeta<TData, TValue>)?.badgeOptions ?? {};

    const option = options[cell.getValue() as string];

    const { iconColor, ...colors } = option?.color ?? { iconColor: undefined };

    return (
        <Badge theme={option?.theme || 'gray'} style={colors}>
            {option?.icon && (
                <FontAwesomeIcon
                    icon={option.icon}
                    className={cn('z-0', !iconColor && !option.theme && 'opacity-50')}
                    style={{ color: iconColor }}
                    size={option.iconSize}
                />
            )}
            {option?.label ? formatMessage(option.label) : String(cell.renderValue())}
        </Badge>
    );
}
